import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, MapPin, Store, LogOut, Building2, RefreshCw, Star, Users, Palette, UtensilsCrossed, ChefHat,
} from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getLocationString = (loc) => {
  if (!loc) return null;
  if (typeof loc === 'string') return loc;
  if (typeof loc === 'object') {
    const parts = [loc.city, loc.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
};

const STATUS_CONFIG = {
  publish: { label: 'Publicado', badgeClass: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30', dotClass: 'bg-green-400' },
  desactive: { label: 'Inactivo', badgeClass: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30', dotClass: 'bg-yellow-400' },
  suspend: { label: 'Suspendido', badgeClass: 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30', dotClass: 'bg-red-400' },
  setup: { label: 'En configuración', badgeClass: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30', dotClass: 'bg-blue-400' },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || {
    label: status || 'Desconocido',
    badgeClass: 'bg-gray-500/20 text-content-muted border-line-active/30',
    dotClass: 'bg-gray-400',
  };

const StatusBadge = ({ status }) => {
  const cfg = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.badgeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
      {cfg.label}
    </span>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const OrgLocationSelector = () => {
  const navigate = useNavigate();
  const { switchRestaurant, logout, currentUser, hasOrg, isGroupOwner, loading: authLoading } = useAuth();

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Guard: solo miembros de una cadena. Esperar a que termine de cargar auth.
  useEffect(() => {
    if (!authLoading && !hasOrg) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasOrg, navigate]);

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const getMyOrgLocationsFn = httpsCallable(functions, 'restaurants-endpoints-getMyOrgLocations');
      const result = await getMyOrgLocationsFn();
      if (result.data.success) {
        setLocations(result.data.locations || []);
      }
    } catch (err) {
      console.error('Error cargando sedes:', err);
      setError('No se pudieron cargar tus sedes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasOrg) fetchLocations();
  }, [hasOrg, fetchLocations]);

  const filteredLocations = useMemo(() => {
    const list = [...locations].sort((a, b) => {
      // La sede primaria primero; luego por nombre de sede.
      if (a.isPrimaryLocation !== b.isPrimaryLocation) return a.isPrimaryLocation ? -1 : 1;
      return (a.locationName || a.name || '').localeCompare(b.locationName || b.name || '');
    });
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (l) =>
        l.locationName?.toLowerCase().includes(q) ||
        l.name?.toLowerCase().includes(q) ||
        getLocationString(l.location)?.toLowerCase().includes(q) ||
        l.id?.toLowerCase().includes(q)
    );
  }, [locations, searchQuery]);

  const brandName = locations[0]?.name || 'Mi Cadena';

  const handleSelectLocation = async (locationId) => {
    await switchRestaurant(locationId);
    navigate(`/${locationId}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3">
      {/* Header */}
      <header className="border-b border-line-subtle bg-surface-chrome/80 dark:bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
              alt="KumIA"
              className="w-10 h-10"
              style={{ filter: 'drop-shadow(0 0 10px rgba(122, 201, 67, 0.5))' }}
            />
            <div>
              <h1 className="text-xl font-bold text-content-primary">{brandName}</h1>
              <div className="flex items-center gap-1.5">
                <Building2 size={12} className="text-kumia-green" />
                <span className="text-xs text-kumia-green font-medium">Gestión de Cadena</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-content-muted hidden sm:inline">{currentUser?.email}</span>
            {isGroupOwner && (
              <button
                onClick={() => navigate('/chain/team')}
                className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-line-strong"
                title="Gestionar gerentes de la cadena"
              >
                <Users size={16} />
                <span className="hidden sm:inline">Equipo</span>
              </button>
            )}
            {isGroupOwner && (
              <button
                onClick={() => navigate('/chain/branding')}
                className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-line-strong"
                title="Branding de marca"
              >
                <Palette size={16} />
                <span className="hidden sm:inline">Branding</span>
              </button>
            )}
            {isGroupOwner && (
              <button
                onClick={() => navigate('/chain/menu')}
                className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-line-strong"
                title="Menú · sucursales conectadas"
              >
                <UtensilsCrossed size={16} />
                <span className="hidden sm:inline">Menú</span>
              </button>
            )}
            {isGroupOwner && (
              <button
                onClick={() => navigate('/chain/recipes')}
                className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-line-strong"
                title="Recetas · sucursales conectadas"
              >
                <ChefHat size={16} />
                <span className="hidden sm:inline">Recetas</span>
              </button>
            )}
            <button
              onClick={fetchLocations}
              disabled={loading}
              className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg text-sm transition-all duration-200 border border-line-strong disabled:opacity-50"
              title="Recargar"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-700 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-red-600/30"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-content-primary mb-2">Mis Sedes</h2>
          <p className="text-content-muted">
            Selecciona una sucursal para gestionarla. Cada sede tiene su propio entorno.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input
            type="text"
            placeholder="Buscar sede por nombre o ubicación..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-surface-3/50 border border-line-strong rounded-xl text-content-primary placeholder-content-subtle focus:outline-none focus:ring-2 focus:ring-kumia-green/50 focus:border-kumia-green/50 transition-all"
          />
          {locations.length > 0 && (
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-content-subtle">
              {filteredLocations.length} de {locations.length}
            </span>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="kumia-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-text">
                <span className="text-content-primary">Kum</span>
                <span className="text-kumia-green">IA</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-500/30 rounded-xl p-6 text-center">
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={fetchLocations}
              className="mt-4 px-4 py-2 bg-red-600/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-600/30 transition-colors border border-red-600/30"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Locations Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLocations.map((location) => (
              <button
                key={location.id}
                onClick={() => handleSelectLocation(location.id)}
                className="group bg-surface-3/40 hover:bg-surface-3/60 border border-line-strong/50 hover:border-line-active/60 rounded-xl p-5 text-left flex items-start gap-4 transition-all duration-200"
              >
                {location.logo ? (
                  <img
                    src={location.logo}
                    alt={location.locationName || location.name}
                    className="w-12 h-12 rounded-lg object-cover bg-surface-5 flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-surface-5/50 flex items-center justify-center flex-shrink-0">
                    <Store size={24} className="text-content-subtle group-hover:text-kumia-green transition-colors" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-semibold text-content-primary group-hover:text-kumia-green transition-colors truncate">
                      {location.locationName || location.name}
                    </h3>
                    {location.isPrimaryLocation && (
                      <Star size={13} className="text-kumia-green flex-shrink-0" title="Sede principal" />
                    )}
                  </div>
                  {getLocationString(location.location) && (
                    <div className="flex items-center gap-1 mt-1 text-sm text-content-subtle">
                      <MapPin size={14} className="flex-shrink-0" />
                      <span className="truncate">{getLocationString(location.location)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <StatusBadge status={location.status} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredLocations.length === 0 && (
          <div className="text-center py-20">
            <Store size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-content-muted text-lg">
              {searchQuery ? 'No se encontraron sedes con ese filtro.' : 'Aún no tienes sedes configuradas.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgLocationSelector;
